/**
 * Shared WhatsApp page layout shells (beautiful-ui).
 *
 * Pick the token that matches the page type — do not invent one-off
 * `max-w-6xl mx-auto` roots on WhatsApp pages.
 *
 * Excluded from beautiful-ui shell migration (leave as-is):
 * - OptInOptOut.jsx
 * - AccountHealth.jsx
 *
 * @see `.cursor/rules/prodyt-ui-theming.mdc` — WhatsApp page shells
 */

export const WA_PAGE_BG = "bg-slate-50 dark:bg-slate-950";

/** Horizontal + vertical padding shared by fluid / hybrid shells. */
export const WA_PAGE_PAD = "px-4 sm:px-6 lg:px-8 py-6 sm:py-8";

/** Vertical rhythm between major page sections. */
export const WA_PAGE_GAP = "space-y-6 sm:space-y-8";

/**
 * Layout class bundles for WhatsApp pages.
 *
 * | Token          | Use |
 * |----------------|-----|
 * | `fluid`        | Dashboards, tables, list pages — fill content column |
 * | `focused`      | Settings / wizards / dense forms — readable max width |
 * | `hybrid`       | Form + preview pages — outer full width |
 * | `focusedInner` | Form column inside a hybrid layout |
 * | `fill`         | Chat / full-height workspace inside MenuLayout |
 * | `fluidCompact` | Fluid without forcing min-h (when parent owns height) |
 *
 * Reference usage:
 * - fluid: Dashboard, Campaigns, Templates, Contacts, Manage Numbers,
 *   Analytics, Campaign Analytics, Billing
 * - fluid + workflowTheme.page: WorkFlows list, Workflow Analytics
 * - hybrid: Send Message, Create Template
 * - focused: Workflow Settings, Basic Automations (standalone)
 * - focusedInner: Create WorkFlow content column
 * - fill: Chat (+ `-m-3 h-full overflow-hidden`)
 */
export const whatsappPageShell = Object.freeze({
  // min-h-full fills the MenuLayout main pane — not 100dvh (that fights the app header and lets tall pages paint under it)
  fluid: `w-full ${WA_PAGE_BG} min-h-full ${WA_PAGE_PAD} ${WA_PAGE_GAP}`,

  focused: `w-full max-w-4xl mx-auto ${WA_PAGE_BG} min-h-full px-4 sm:px-6 py-6 sm:py-8 space-y-6`,

  hybrid: `w-full ${WA_PAGE_BG} min-h-full ${WA_PAGE_PAD} ${WA_PAGE_GAP}`,

  focusedInner: "w-full max-w-4xl",

  fill: `w-full h-full min-h-0 ${WA_PAGE_BG}`,

  fluidCompact: `w-full ${WA_PAGE_BG} ${WA_PAGE_PAD} ${WA_PAGE_GAP}`,
});

/**
 * Join shell token(s) with optional extra classes.
 * @param {...(string|false|null|undefined)} parts
 * @returns {string}
 */
export function waShell(...parts) {
  return parts.filter(Boolean).join(" ");
}
